<template>
  <BModal
    :visible="value"
    hide-footer
    hide-header
    centered
    lazy
    content-class="settings-modal"
    @change="$emit('input', $event)"
    @show="reset"
  >
    <div class="d-flex align-items-center justify-content-between">
      <div class="modal-title">
        {{ $t('menu.settings') }}
      </div>
      <button
        type="button"
        class="close-btn"
        @click="close"
      >
        <i class="fa-regular fa-xmark" />
      </button>
    </div>

    <div class="subsection-title mt-3">
      {{ $t('shop.settings.todayOrder.payerFields') }}
    </div>
    <div class="small text-secondary">
      {{ $t('shop.settings.todayOrder.payerFieldsInfo') }}
    </div>

    <div class="fields-list mt-3">
      <div
        v-for="p in payerProperties"
        :key="p.code"
        class="field-row d-flex align-items-center justify-content-between"
      >
        <div class="field-name">
          {{ p.name }}
        </div>
        <BFormCheckbox
          switch
          size="lg"
          :disabled="pending"
          :checked="visible.includes(p.code)"
          @change="toggleProperty(p.code, $event)"
        />
      </div>
      <div
        v-if="payerProperties.length === 0"
        class="field-row small text-secondary"
      >
        {{ $t('shop.settings.todayOrder.noPayerFields') }}
      </div>
    </div>

    <div class="fields-list mt-3">
      <div class="field-row">
        <div class="field-name">
          {{ $t('shop.settings.todayOrder.braceletField') }}
        </div>
        <div class="small text-secondary mb-2">
          {{ $t('shop.settings.todayOrder.braceletFieldInfo') }}
        </div>
        <BFormSelect
          v-model="braceletPayerProperty"
          :options="braceletOptions"
          :disabled="pending"
        />
      </div>
    </div>

    <div class="fields-list mt-3">
      <div class="field-row d-flex align-items-center justify-content-between">
        <div>
          <div class="field-name">
            {{ $t('shop.settings.todayOrder.showMealTitle') }}
          </div>
          <div class="small text-secondary">
            {{ $t('shop.settings.todayOrder.showMealTitleInfo') }}
          </div>
        </div>
        <BFormCheckbox
          v-model="showMealTitle"
          switch
          size="lg"
          :disabled="pending"
        />
      </div>
      <div class="field-row d-flex align-items-center justify-content-between">
        <div>
          <div class="field-name">
            {{ $t('shop.settings.todayOrder.fastEdit') }}
          </div>
          <div class="small text-secondary">
            {{ $t('shop.settings.todayOrder.fastEditInfo') }}
          </div>
        </div>
        <BFormCheckbox
          :checked="screenVariant === 'FastEdit'"
          switch
          size="lg"
          :disabled="pending"
          @change="screenVariant = $event ? 'FastEdit' : 'Simple'"
        />
      </div>
    </div>

    <div
      v-if="error"
      class="text-danger small mt-2"
    >
      {{ $t('error.error') }}
    </div>

    <div class="actions d-flex mt-4">
      <button
        type="button"
        class="action-btn cancel"
        :disabled="pending"
        @click="close"
      >
        <i class="fa-regular fa-xmark pr-2" />
        {{ $t('general.cancel') }}
      </button>
      <button
        type="button"
        class="action-btn confirm"
        :disabled="pending"
        @click="save"
      >
        <i
          v-if="pending"
          class="fas fa-spinner fa-spin pr-2"
        />
        <i
          v-else
          class="fa-regular fa-check pr-2"
        />
        {{ $t('general.save') }}
      </button>
    </div>
  </BModal>
</template>

<script>
import { isScanProperty } from '@/utils/payer-properties';
import { mapActions, mapGetters } from 'vuex';

export default {
  props: {
    // Modal visibility, use with v-model
    value: Boolean,
    // Today's order config currently in use
    config: Object,
  },
  data: () => ({
    visible: [],
    showMealTitle: true,
    screenVariant: 'Simple',
    braceletPayerProperty: null,
    pending: false,
    error: false,
  }),
  computed: {
    ...mapGetters(['properties']),
    payerProperties() {
      return this.properties
        .filter((p) => !isScanProperty(p) && p.code !== this.braceletPayerProperty);
    },
    braceletOptions() {
      return [
        { value: null, text: this.$t('shop.settings.todayOrder.noBraceletField') },
        ...this.properties.map((p) => ({ value: p.code, text: p.name })),
      ];
    },
    changed() {
      const savedVisible = this.config?.visiblePayerProperties || [];
      return this.showMealTitle !== (this.config?.showMealTitle ?? true)
        || this.screenVariant !== (this.config?.screenVariant || 'Simple')
        || this.braceletPayerProperty !== (this.config?.braceletPayerProperty || null)
        || this.visible.length !== savedVisible.length
        || this.visible.some((c) => !savedVisible.includes(c));
    },
  },
  methods: {
    ...mapActions('shop', ['updateTodayOrderConfig']),
    toggleProperty(code, visible) {
      this.visible = visible
        ? [...this.visible, code]
        : this.visible.filter((c) => c !== code);
    },
    reset() {
      this.visible = [...(this.config?.visiblePayerProperties || [])];
      this.showMealTitle = this.config?.showMealTitle ?? true;
      this.screenVariant = this.config?.screenVariant || 'Simple';
      this.braceletPayerProperty = this.config?.braceletPayerProperty || null;
      this.error = false;
    },
    close() {
      this.$emit('input', false);
    },
    save() {
      if (!this.changed) {
        this.close();
        return;
      }

      this.pending = true;
      this.error = false;
      this.updateTodayOrderConfig({
        data: {
          visiblePayerProperties: this.visible,
          showMealTitle: this.showMealTitle,
          screenVariant: this.screenVariant,
          braceletPayerProperty: this.braceletPayerProperty,
        },
      })
        .then(({ data }) => {
          this.$emit('updated', data.todayOrder);
          this.close();
        })
        .catch(() => {
          this.error = true;
        })
        .finally(() => {
          this.pending = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .settings-modal {
  border-radius: 16px;
  border: none;

  .modal-body {
    padding: 24px 28px;
  }
}

.modal-title {
  font-size: 26px;
  font-weight: 600;
}

.close-btn {
  border: none;
  background: none;
  font-size: 28px;
  line-height: 1;
  padding: 0 4px;
  color: inherit;
}

.subsection-title {
  font-size: 18px;
  font-weight: 600;
}

.fields-list {
  border: 1px solid #eee;
  border-radius: 12px;
  overflow: hidden;
}

.field-row {
  padding: 16px 20px;
  gap: 16px;

  & + .field-row {
    border-top: 1px solid #eee;
  }
}

.field-name {
  font-weight: 600;
}

.actions {
  gap: 10px;
}

.action-btn {
  flex: 1;
  height: 50px;
  border: none;
  border-radius: 25px;
  color: white;
  font-size: 20px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;

  &.cancel {
    background-color: #9a9a9a;
  }

  &.confirm {
    background-color: $blue;
  }

  &:disabled {
    opacity: 0.6;
  }
}
</style>
